<template lang="pug">
div(style="display: none")
</template>
<script lang="ts">
import store from "@/store";
import {
  AudioLevelUpdatedEvent,
  Group,
  StreamCreatedEvent,
  StreamDestroyedEvent,
  VideoElementCreatedEvent,
  VideoDisabledWarningEvent,
  VideoDisabledWarningLiftedEvent,
  VideoEnabledChangedEvent,
} from "@/types";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import OT from "@opentok/client";
import authorization from "@/auth/authorization";
import { mapActions, mapGetters, mapState } from "vuex";
import { sockets } from "@/services/socket_api";
import VonageUtil from "./vonageUtil";
import { ApplyUserVideoFilter, MediaOnly, SetPublisher, VideoContainerId } from "@/types/vonage";
import WebMediaUtil from "@/utils/WebMediaUtil";
import CommonUtil from "@/utils/CommonUtil";
import Globals from "@/utils/Globals";
import Authorization from "@/auth/authorization";
import employeeLogo from "@/assets/images/logos/Crucial-C.svg";
import { EventContentModel } from "@cruciallearning/puddle";
import { genericErrors, publishErrors } from "./ErrorHandler";
import log from "./DebugLogger";

/** locally-common type extracted for re-use below */
type InsertModeType = "append" | "replace" | "after" | "before" | undefined;

@Component({
  computed: {
    ...mapState(["selectedAudioId", "selectedVideoId"]),
    ...mapGetters("BreakoutsModule", ["getActiveBreakoutSelf"]),
    ...mapState("EventModule", ["event"]),
    ...mapState("MediaModule", ["hasAudio", "hasVideo", "isBlurred"]),
    ...mapState("VonageModule", ["session"]),
  },
  methods: {
    ...mapActions("VonageModule", ["setPublisher", "applyUserVideoFilter"]),
  },
})
export default class Publisher extends Vue {
  @Prop({ required: false, default: false }) isBreakout!: boolean;
  @Prop({ required: false, default: false }) isSysCheck!: boolean;
  @Prop({ required: false, default: undefined }) mediaOnly!: MediaOnly | undefined;
  @Prop({ required: true }) token!: string;
  private muteIconSrc!: string;
  private readonly event!: EventContentModel;
  private readonly getActiveBreakoutSelf!: Group | undefined;

  private readonly hasAudio!: boolean;
  private readonly hasVideo!: boolean;
  private readonly isBlurred!: boolean;

  private readonly selectedAudioId!: string;
  private readonly selectedVideoId!: string;

  private readonly session!: OT.Session | null;

  private setPublisher!: SetPublisher;
  private applyUserVideoFilter!: ApplyUserVideoFilter;

  private audioLevel = 0;

  mounted(): void {
    this.sessionUpdate();
  }

  async init(
    insertMode: InsertModeType = undefined,
    targetElement: string | undefined = void 0,
    insertDefaultUI = false,
    showControls = false
  ): Promise<OT.Publisher> {
    const options: OT.PublisherProperties = await this._makePublisherProperties(
      insertMode,
      insertDefaultUI,
      showControls
    );
    const _publisher = OT.initPublisher(targetElement, options, (error) => {
      genericErrors(error);
      if (error) this.$emit("publishError", error);
    });
    this._registerEventListeners(_publisher);
    return Promise.resolve(_publisher);
  }

  /** init OT.PublisherProperties using "normal" or "CLEXP-5532" logic as appropriate */
  async _makePublisherProperties(
    insertMode: InsertModeType = undefined,
    insertDefaultUI = false,
    showControls = false
  ): Promise<OT.PublisherProperties> {
    let options: OT.PublisherProperties;
    if (WebMediaUtil.getClexp5532Enabled()) {
      console.log("CLEXP-5532 experiment is enabled; using revised OT.initPublisher logic.");
      options = await this.__makePublisherPropertiesClexp5532(insertMode, insertDefaultUI, showControls);
    } else {
      console.log("CLEXP-5532 experiment is NOT enabled; using previous OT.initPublisher logic.");
      options = this.__makePublisherPropertiesNormal(insertMode, insertDefaultUI, showControls);
    }
    return Promise.resolve(options);
  }

  get audioSourceOption(): string | false | undefined {
    if (this.mediaOnly === "video") return false;
    return this.selectedAudioId || undefined;
  }

  get videoSourceOption(): string | false | undefined {
    if (this.mediaOnly === "audio") return false;
    return this.selectedVideoId || undefined;
  }

  /** init OT.PublisherProperties using the traditional (non-CLEXP-5532) logic */
  __makePublisherPropertiesNormal(
    insertMode: InsertModeType,
    insertDefaultUI: boolean,
    showControls: boolean
  ): OT.PublisherProperties {
    const options: OT.PublisherProperties = {
      insertDefaultUI,
      insertMode,
      name: this.$auth.authUser.id,
      fitMode: "cover",
      enableDtx: true,
      publishVideo: false,
      publishAudio: false,
      showControls,
      echoCancellation: true,
      noiseSuppression: true,
      audioSource: this.audioSourceOption,
      videoSource: this.videoSourceOption,
      mirror: true,
      audioFallback: {
        publisher: true,
        subscriber: true,
      },
    };
    if (OT.hasMediaProcessorSupport()) {
      console.log("OT.hasMediaProcessorSupport() enabled, adding advancedNoiseSuppression audio filter");
      options.audioFilter = {
        type: "advancedNoiseSuppression",
      };
    }
    return options;
  }

  /** init OT.PublisherProperties using the localStore-override-able CLEXP-5532 logic */
  async __makePublisherPropertiesClexp5532(
    insertMode: InsertModeType,
    insertDefaultUI: boolean,
    showControls: boolean
  ): Promise<OT.PublisherProperties> {
    const mediaStream: MediaStream = await WebMediaUtil.getUserMedia(null, null);
    const audioSource: MediaStreamTrack = mediaStream.getAudioTracks()[0];
    const videoSource: MediaStreamTrack = mediaStream.getVideoTracks()[0];
    console.log("MediaStream:", mediaStream, audioSource, videoSource);
    WebMediaUtil.debugLogMediaStreamTracks(mediaStream);
    Globals.addGlobal("mediaStream", mediaStream);
    Globals.addGlobal("videoSource", videoSource);
    Globals.addGlobal("audioSource", audioSource);
    const options: OT.PublisherProperties = this.__resolveClexp5532PublisherProperties({
      insertDefaultUI,
      insertMode,
      showControls,
      name: this.$auth.authUser.id,
      audioSource: audioSource,
      videoSource: videoSource,
    });
    return Promise.resolve(options);
  }

  /** merge `given`, localStore and default OT.PublisherProperties objects to determine active config for the CLEXP-5532 case */
  __resolveClexp5532PublisherProperties(given: object): OT.PublisherProperties {
    const local: object = WebMediaUtil.getLocalPublisherProperties() ?? {};
    const defaults: object = this.__makeBaseClexp5532PublisherProperties() ?? {};
    const merged: object = CommonUtil.merge(given, defaults, local);
    console.log(CommonUtil.jsonppp("Publisher.__resolveClexp5532PublisherProperties(): ", merged));
    return merged;
  }

  /** base (default) OT.PublisherProperties for the CLEXP-5532 case */
  __makeBaseClexp5532PublisherProperties(): OT.PublisherProperties {
    return {
      // streams
      publishVideo: false,
      publishAudio: false,
      // video
      fitMode: "cover",
      scalableVideo: false,
      mirror: true,
      // audio
      enableDtx: true,
    };
  }

  /** add the many `publisher.on` event handlers to the given instance */
  _registerEventListeners(publisher: OT.Publisher): void {
    publisher?.on({
      videoElementCreated: (event: VideoElementCreatedEvent) => {
        log("Publisher Video Element created ", event);
        if (event.target.element) {
          const securityId = VonageUtil.userClass(this.$auth.authUser.id);
          event.target.element.classList.add("user-video", "speaker", `user-video-${securityId}`);
        }
        if (!this.isBreakout) {
          if (event.target.element) {
            event.target.element.style.width = "45%";
            event.target.element.style.height = "0";
            event.target.element.style.paddingBottom = "35%";
          }
        }
        if (this.isBlurred) {
          this.applyUserVideoFilter(publisher);
        }
      },
      accessDenied: (): void => {
        if (!this.isBreakout) store.dispatch("update", { isMediaAllowed: false });
      },
      streamCreated: (event: StreamCreatedEvent): void => {
        log("Publisher Stream Created", event);
        sockets.stream.updateStreamId(event.target.stream?.streamId || "MISSING");
        const securityId = this.$auth.authUser.id;
        if (event.target.stream?.connection) {
          const node = VonageUtil.userNameNode(securityId, event.target.stream?.connection.data);
          const mutedNode = VonageUtil.mutedIndicatorNode(event.target.stream.name, this.muteIconSrc);
          if (node) {
            event.target.element?.append(node);
            event.target.element?.append(mutedNode);
          }
          if (Authorization.isObserver()) {
            const iconNode = VonageUtil.employeeIndicatorNode(securityId, employeeLogo);
            event.target.element?.append(iconNode);
          }
          if (event.target.element && this.isBreakout) {
            const initialNode = VonageUtil.hiddenVideoNode(event.target.stream?.connection.data);
            if (initialNode) {
              event.target.element.append(initialNode);
            }
          }
        }
        if (event.target) {
          const securityId = this.$auth.authUser.id;
          (event.target as OT.Publisher).publishVideo(this.hasVideo);
          if (securityId && authorization.isManager(securityId, this.event)) {
            (event.target as OT.Publisher).publishAudio(this.hasAudio);
          }
        }
        this.setPublisher(event.target as OT.Publisher);
      },
      streamDestroyed: (event: StreamDestroyedEvent): void => {
        if (event.reason === "networkDisconnected") console.log("The publisher lost it's connection for user:");
        log("Publisher stream destroyed", event);
      },
      muteForced: (event: unknown): void => {
        log("Publisher mute forced", event);
      },
      audioLevelUpdated: (event: AudioLevelUpdatedEvent): void => {
        if (this.audioLevel > event.audioLevel && event.audioLevel > 0.1) {
          if (!this.isBreakout) {
            if (this.$auth.authUser.id !== store.state.activeSpeakerId) {
              sockets.general.send("", "activeSpeaker.claim");
            }
          } else {
            if (this.$auth.authUser.id !== store.state.breakoutActiveSpeakerId) {
              if (this.getActiveBreakoutSelf) {
                this.getActiveBreakoutSelf.users?.forEach((user) => {
                  sockets.general.send("", "activeSpeaker.claim.breakout", user.securityId);
                });
              }
            }
          }
        }
        this.audioLevel = event.audioLevel;
      },
      videoEnabled: (event: VideoEnabledChangedEvent): void => {
        log("Publisher video enabled; reason=" + event?.reason, event);
      },
      videoDisabled: (event: VideoEnabledChangedEvent): void => {
        log("Publisher video disabled; reason=" + event?.reason, event);
      },
      // note yes the event _class_ really is "…Disabled…" while the event _name_ is just "…Disable…"
      videoDisableWarning: (event: VideoDisabledWarningEvent): void => {
        log("Publisher video disabled warning; reason=" + event?.reason, event);
      },
      // note yes the event _class_ really is "…Disabled…" while the event _name_ is just "…Disable…"
      videoDisableWarningLifted: (event: VideoDisabledWarningLiftedEvent): void => {
        log("Publisher video disabled warning lifted; reason=" + event?.reason, event);
      },
    });
  }

  @Watch("session")
  sessionUpdate(): void {
    if (!this.session) return;
    this.session?.connect(this.token, async (error) => {
      if (error) genericErrors(error);
      else {
        let id = "";
        if (this.isBreakout) {
          id = "breakout-users";
        } else if (this.isSysCheck) {
          id = "system-check-camera";
        } else {
          let group = VideoContainerId.LEARNERS;
          const securityId = this.$auth.authUser.id;
          if (securityId && authorization.isTrainer(securityId, this.event)) {
            group = VideoContainerId.TRAINERS;
          }
          if (
            (securityId && authorization.isModerator(securityId, this.event)) ||
            authorization.isObserver(securityId, this.event)
          ) {
            group = VideoContainerId.MODERATORS;
          }
          id = `${group}-video-container`;
        }
        const publisher = await this.init("append", id, true);
        this.session?.publish(publisher, publishErrors);
        this.muteIconSrc = await VonageUtil.getMuteIconSrc();
      }
    });
  }
}
</script>
